import React, { useEffect, useMemo, useState } from "react";
import type { CalendarItem } from "@nelurio/contracts";
import { Badge, Card, Icon, IconButton } from "@nelurio/ui";
import { persianMonthDays, useI18n } from "@nelurio/i18n";

const typeTone: Record<string, "purple" | "blue" | "coral" | "amber" | "green"> = {
  review: "purple",
  new_lesson: "blue",
  exam: "coral",
  assignment: "amber",
  class: "green",
  study: "purple",
  project: "blue"
};

/** Local-time date key — toISOString would shift the day after midnight. */
export function localDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

interface CalendarMonthProps {
  items: CalendarItem[];
  compact?: boolean;
  selected?: string;
  onSelect?: (dateKey: string) => void;
  onRangeChange?: (from: string, to: string) => void;
}

export function CalendarMonth({ items, compact = false, selected, onSelect, onRangeChange }: CalendarMonthProps) {
  const { locale, calendar, formatDate, formatDayNumber, formatNumber, t } = useI18n();
  const [cursor, setCursor] = useState(() => new Date());

  // Gregorian grid stays the data backbone; Persian regions get a Jalali grid
  // derived from the same cursor so the whole month renders in the Solar Hijri.
  const grid = useMemo(() => {
    if (calendar === "persian") {
      const days = persianMonthDays(cursor);
      return {
        first: days[0] ?? cursor,
        cells: Array.from({ length: Math.ceil((days.length + ((days[0]?.getDay() + 1) % 7)) / 7) * 7 }, (_, index) => {
          const offset = index - ((days[0]?.getDay() + 1) % 7);
          return offset >= 0 && offset < days.length ? days[offset] : null;
        })
      };
    }
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const mondayIndex = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return {
      first,
      cells: Array.from({ length: 42 }, (_, index) => {
        const day = index - mondayIndex + 1;
        return day > 0 && day <= daysInMonth ? new Date(year, month, day) : null;
      })
    };
  }, [calendar, cursor]);

  const first = grid.first;

  const activeSelected = selected ?? localDateKey(new Date());

  useEffect(() => {
    if (!onRangeChange) return;
    const gridDays = grid.cells.filter((day): day is Date => Boolean(day));
    const rangeStart = gridDays[0] ?? first;
    const rangeEnd = gridDays[gridDays.length - 1] ?? first;
    onRangeChange(localDateKey(rangeStart), localDateKey(rangeEnd));
  }, [first, grid.cells, onRangeChange]);

  const grouped = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of items) {
      const list = map.get(item.date) || [];
      list.push(item);
      map.set(item.date, list);
    }
    return map;
  }, [items]);

  // Deterministic agenda order: timed items first (by time), then exams,
  // reviews, tasks, routines — never random DB order.
  const typeRank: Record<string, number> = { exam: 0, review: 1, task: 2, routine: 3 };
  const selectedItems = (grouped.get(activeSelected) || []).slice().sort((a, b) => {
    if (a.time && b.time) return a.time.localeCompare(b.time);
    if (a.time) return -1;
    if (b.time) return 1;
    const rank = (typeRank[a.type] ?? 9) - (typeRank[b.type] ?? 9);
    if (rank !== 0) return rank;
    return a.title.localeCompare(b.title);
  });
  const agendaCap = compact ? 3 : 6;
  const agendaHidden = Math.max(0, selectedItems.length - agendaCap);
  // Persian weeks start on Saturday; every other region on Monday.
  const weekDays = useMemo(() => {
    const startDay = calendar === "persian" ? 6 : 1; // 2024-01-06 is a Saturday, 2024-01-01 a Monday
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "narrow" });
    return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2024, 0, startDay + index)));
  }, [calendar, locale]);

  // Keep the day-of-month when stepping so Jalali month navigation stays aligned.
  const changeMonth = (delta: number) => {
    const day = Math.min(cursor.getDate(), 28);
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, day));
  };

  return (
    <Card className={`month-calendar ${compact ? "month-calendar--compact" : ""}`} elevated>
      <div className="month-calendar__header">
        <div>
          <span className="section-kicker">{t("dashboard.calendar")}</span>
          <strong>{formatDate(first, { month: "long", year: "numeric" })}</strong>
        </div>
        <div className="month-calendar__controls">
          <IconButton label={t("calendar.prevMonth")} icon="chevron" onClick={() => changeMonth(-1)} />
          <IconButton label={t("calendar.nextMonth")} icon="chevron" className="month-calendar__next" onClick={() => changeMonth(1)} />
        </div>
      </div>

      <div className="month-calendar__weekdays">
        {weekDays.map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
      </div>
      <div className="month-calendar__grid">
        {grid.cells.map((date, index) => {
          if (!date) return <span className="month-calendar__empty" key={`empty-${index}`} />;
          const key = localDateKey(date);
          const dayItems = grouped.get(key) || [];
          const isToday = key === localDateKey(new Date());
          const isSelected = key === activeSelected;
          return (
            <button
              type="button"
              className={`month-calendar__day ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""}`}
              key={key}
              onClick={() => onSelect?.(key)}
              aria-label={`${formatDate(date)}, ${formatNumber(dayItems.length)} ${t("calendar.events")}`}
            >
              <span>{formatDayNumber(date)}</span>
              <i className="month-calendar__dots">
                {dayItems.slice(0, 3).map((item) => <b key={item.id} data-tone={typeTone[item.type] || "purple"} />)}
              </i>
            </button>
          );
        })}
      </div>

      <div className="month-calendar__agenda">
        <div className="month-calendar__agenda-title">
          <strong>{t("calendar.eventsFor")} {formatDate(`${activeSelected}T12:00:00`, { month: "short", day: "numeric" })}</strong>
          <Badge tone="neutral">{formatNumber(selectedItems.length)}</Badge>
        </div>
        {selectedItems.length ? selectedItems.slice(0, agendaCap).map((item) => (
          <div className="calendar-agenda-item" key={item.id}>
            <span className={`calendar-agenda-item__icon tone-${typeTone[item.type] || "purple"}`}><Icon name={item.type === "exam" ? "warning" : item.type === "review" ? "route" : "clock"} size={15} /></span>
            <div>
              <strong>{item.title}</strong>
              <small>{item.time || t("common.noTime")}{item.courseTitle ? ` · ${item.courseTitle}` : ""}</small>
            </div>
          </div>
        )) : <p className="month-calendar__empty-text">{t("dashboard.emptyToday")}</p>}
        {agendaHidden > 0 && (
          <div className="month-calendar__more">+{formatNumber(agendaHidden)}</div>
        )}
      </div>
    </Card>
  );
}
